import { api } from '@/api';
import {
  DeviceMeasurementErrorDto,
  instanceOfRunBeamConstancyMeasurementRequestDto,
  MeasurementStatus,
  MeasurementStatusDto,
  ResultDto,
  RunMeasurementRequestDto,
} from '@/api/generator';
import { AnyApiError, ApiError } from '@/errorHandling/typeGuards';
import { NotificationEntry } from '@/components/notifications/types';
import { notificationForDeviceError } from '@/components/notifications/utils';
import { injectStrict } from '@/utils/injectStrict';
import { useHubSubscription } from '@/utils/signalR';
import { HubConnectionBuilder } from '@microsoft/signalr';
import { useMemoize, createEventHook } from '@vueuse/core';
import {
  computed,
  getCurrentScope,
  MaybeRefOrGetter,
  onScopeDispose,
  Ref,
  ref,
  toRef,
  toValue,
  watch,
} from 'vue';
import { MeasurableTaskType, MeasurementEssentials } from '../types';
import { WorkflowCommunicationHubKey } from './types';
import { severityOrder, MAX_ACCEPTABLE_SEVERITY } from '@/components/deviceStatus/utils/severity';
import { truthy } from '@/utils/helper';

/** A measurement is active while it has a status that is not terminal (Finished or Error). */
export function isActiveMeasurementStatus(status?: MeasurementStatus | null): boolean {
  return !!status && status !== 'Finished' && status !== 'Error';
}

export function hasUnacceptableErrors(messages: DeviceMeasurementErrorDto[]): boolean {
  if (!messages?.length) return false;

  return messages.some((message) => {
    if (!message.severity) return false;

    const maxAcceptableSeverityLevel = severityOrder.indexOf(MAX_ACCEPTABLE_SEVERITY);
    const severityIndex = severityOrder.indexOf(message.severity);
    // severityOrder is sorted from highest to lowest severity
    return severityIndex >= 0 && severityIndex < maxAcceptableSeverityLevel;
  });
}

// Must match Ptw.Ptw100.AppZero.Application.Common.Hubs.HubEventNames.WorkflowMeasurementUpdated.
const WORKFLOW_MEASUREMENT_UPDATED_EVENT = 'WorkflowMeasurementUpdated';

export function getWorkflowCommunicationHubConnectionSignalR() {
  const interval = 1; // in seconds

  const connection = new HubConnectionBuilder()
    .withUrl('/workflowCommunicationHub')
    .withAutomaticReconnect({
      nextRetryDelayInMilliseconds: () => interval * 1000,
    })
    .build();

  // The hub is event-based (RegisterToWorkflow itself is a plain RPC call, not a stream): the
  // server pushes updates to all registered clients via this named event instead.
  connection.on(WORKFLOW_MEASUREMENT_UPDATED_EVENT, (value: MeasurementStatusDto) => {
    if (value.measurementId) {
      cachedStateValue(value.measurementId).value = value;
    }
  });

  return connection;
}

// Both controllers are generated from the same contract, so their run and stop methods share one
// signature. Deriving it from both keeps that assumption checked: if one route ever declares a
// different response or error set, the lookup below stops compiling instead of silently narrowing.
type RunMeasurementFn =
  | typeof api.bccMeasurementsApi.runBccMeasurement
  | typeof api.zeroMeasurementsApi.runZeroMeasurement;
type StopMeasurementFn =
  | typeof api.bccMeasurementsApi.stopBccMeasurement
  | typeof api.zeroMeasurementsApi.stopZeroMeasurement;

interface MeasurementClient {
  run: (
    runMeasurementRequestDto: RunMeasurementRequestDto,
    errorHandlers: Parameters<RunMeasurementFn>[1],
  ) => ReturnType<RunMeasurementFn>;
  stop: (measurementId: string) => ReturnType<StopMeasurementFn>;
}

// The backend exposes one measurement controller per task type with identical request, response
// and error contracts. Only the route differs, so the task type picks the generated client.
const measurementClients: Record<MeasurableTaskType, MeasurementClient> = {
  BeamConstancyCheck: {
    run: (runMeasurementRequestDto, errorHandlers) =>
      api.bccMeasurementsApi.runBccMeasurement({ runMeasurementRequestDto }, errorHandlers),
    stop: (measurementId) => api.bccMeasurementsApi.stopBccMeasurement({ measurementId }),
  },
  BeamAnalysis: {
    run: (runMeasurementRequestDto, errorHandlers) =>
      api.zeroMeasurementsApi.runZeroMeasurement({ runMeasurementRequestDto }, errorHandlers),
    stop: (measurementId) => api.zeroMeasurementsApi.stopZeroMeasurement({ measurementId }),
  },
};

export function useMeasurement<T extends MeasurementEssentials>(
  measurement: MaybeRefOrGetter<T | undefined>,
) {
  const measurementRef = toRef(measurement);

  const measurementId = ref<string | undefined>(undefined);

  const streamId = ref<string | undefined>(undefined);

  const status = useMeasurementStatusSignalR(streamId);

  const finishHook = createEventHook<ResultDto | undefined>();

  const messages = ref<DeviceMeasurementErrorDto[]>([]);

  const startMeasurementError = ref<DeviceMeasurementErrorDto>();

  watch(
    () => status.value?.messages,
    (newMessages) => {
      if (newMessages) {
        messages.value = newMessages;
      }
    },
    {
      immediate: true,
    },
  );

  const notifications = computed<NotificationEntry[]>(() => {
    return [startMeasurementError.value, ...messages.value].filter(truthy).map((message) => ({
      ...notificationForDeviceError(message),
      closable: false,
    }));
  });

  const isMeasuring = computed(() => {
    switch (status.value?.measurementStatus) {
      case 'BeamDetected':
      case 'ProcessingResults':
        return true;
      default:
        return false;
    }
  });

  const cancelable = computed(
    () => !!status.value?.measurementStatus && status.value?.measurementStatus !== 'Finished',
  );

  // Payload for the 422 lives at `.exception.error` on the generated DTO.
  function startMeasurementErrorHandler(error: AnyApiError) {
    if (error.name === ApiError.deviceMeasurement) {
      startMeasurementError.value = error.error?.exception?.error;
    } else {
      return error;
    }
  }

  // The client that started the running measurement; cancel must go to the same controller.
  let activeClient: MeasurementClient | undefined;

  // Counts starts and resets, so a start answered after a reset does not revive the run.
  let generation = 0;

  async function start() {
    const current = toValue(measurement);

    if (!current?.request) {
      return;
    }

    activeClient = measurementClients[current.taskType];
    const own = ++generation;

    const { result } = await activeClient.run(current.request, [startMeasurementErrorHandler]);

    // Reset meanwhile (the run was cancelled while still starting): the run is not tracked,
    // but its id is still handed on so a cancel chained on this start reaches it.
    if (own !== generation) {
      return result?.measurementId;
    }

    streamId.value = result?.measurementId;

    if (result && measurementRef.value) {
      measurementId.value = result.measurementId;
      startMeasurementError.value = undefined;
      return measurementId.value;
    }
  }

  async function getResult() {
    if (measurementId.value) {
      const { result: measurementResult } = await api.resultsApi.getResultByMeasurementId({
        measurementId: measurementId.value,
      });

      return measurementResult;
    }
  }

  let startPromise: Promise<string | undefined> | undefined;

  const startPending = ref(false);

  // From the start call until the first status of the run arrives: the request is in flight,
  // or answered but the stream has not delivered yet. A caller blocks a second start
  // meanwhile, since it would overwrite the tracked run and leave the first one unattended.
  const starting = computed(() => startPending.value || (!!measurementId.value && !status.value));

  async function safeStart() {
    startPending.value = true;
    try {
      startPromise = start();
      await startPromise;
    } finally {
      startPending.value = false;
    }
  }

  async function safeCancel() {
    return startPromise?.then(cancel);
  }

  async function cancel(measurementId: string | undefined) {
    const current = toValue(measurement);
    const client = activeClient ?? (current && measurementClients[current.taskType]);

    if (measurementId && client) {
      const { success } = await client.stop(measurementId);

      return success;
    } else {
      console.warn('Unable to cancel measurement, no measurementId, start might have failed');
    }
  }

  function reset() {
    generation++;
    activeClient = undefined;
    startPromise = undefined;
    streamId.value = undefined;
    measurementId.value = undefined;
    messages.value = [];
  }

  // Leaving the scope while a run is held (not yet reset) stops it, so no caller has to
  // remember to. Chaining on the start promise instead of the status covers a start whose
  // answer is still pending: without an id there is no stream registered yet whose
  // unregistration would stop the device, so the measurement would run to its end
  // unattended. A stop for a run the backend already dropped is a no-op there.
  if (getCurrentScope()) {
    onScopeDispose(() => {
      safeCancel().catch((error) => {
        console.warn('Unable to cancel measurement on dispose', error);
      });
    });
  }

  watch(
    () => status.value?.measurementStatus,
    async (newStatus) => {
      if (newStatus === 'Finished') {
        const measurementResult =
          measurementRef.value?.request &&
          instanceOfRunBeamConstancyMeasurementRequestDto(measurementRef.value.request)
            ? await getResult()
            : undefined;

        finishHook.trigger(measurementResult);
      }
    },
  );

  return {
    start: safeStart,
    cancel: safeCancel,
    reset,
    cancelable,
    status,
    starting,
    isMeasuring,
    notifications,
    messages,
    onFinish: finishHook.on,
    measurement: measurementRef,
    measurementId,
    request: computed(() => toValue(measurement)?.request),
  };
}

const cachedStateValue = useMemoize((_deviceId: string | undefined) => {
  return ref<MeasurementStatusDto | undefined>();
});

function useMeasurementStatusSignalR(
  streamId: MaybeRefOrGetter<string | undefined>,
): Ref<MeasurementStatusDto | undefined> {
  const connection = injectStrict(WorkflowCommunicationHubKey);

  const id = toRef(streamId);

  useHubSubscription<MeasurementStatusDto>({
    connection,
    event: 'WorkflowMeasurementUpdated',
    key: id,
    onMessage: (value) => {
      if (value.measurementId) {
        cachedStateValue(value.measurementId).value = value;
      }
    },
    subscribe: (key) => connection.invoke('RegisterToWorkflow', key),
    unsubscribe: (token) => connection.invoke('UnregisterFromWorkflow', token),
  });

  return computed(() => {
    return cachedStateValue(id.value).value;
  });
}
